// src/features/records/components/ShareDialog/ShareDialog.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import PeopleGroupPicker from './components/PeopleGroupPicker';
import RoleSelect from './components/RoleSelect';
import ManageAccessTable from './components/ManageAccessTable';
import NotifyToggle from './components/NotifyToggle';
import NotifyTextarea from './components/NotifyTextarea';
import Button from '../../../../components/Button';
import accessControlService from '../../services/accessControlService';
import useUser from '../../../../hooks/useUser';
import { useToast } from '../../../../hooks/useToast';
import useFocusTrap from '../../../../hooks/useFocusTrap';
import useBodyScrollLock from '../../../../hooks/useBodyScrollLock';
import mapRecordsErrorToKey from '../../utils/recordsErrorMapper';
import styles from './ShareDialog.module.css';

const ShareDialog = ({ record, onClose }) => {
  const { t } = useTranslation();
  const { user } = useUser();
  const toast = useToast();
  const stageRef = useRef(null); // NEW: ref for stage wrapper
  const overlayRef = useRef(null); // NEW: ref for overlay
  const initRequestRef = useRef(0);
  const initializedSessionRef = useRef(null);
  
  // Compute reduced motion preference synchronously for initial render
  const reduceMotion =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  const recordId = record?.id || null;
  const ownerId = record?.access?.owner || null;
  const viewerId = user?.uid || null;
  
  // NEW: presence / transition state
  const [mounted, setMounted] = useState(false); // false → 'closed' (offscreen)
  const [isClosing, setIsClosing] = useState(false); // true → animate out
  
  // State for quick add section
  const [selectedPrincipals, setSelectedPrincipals] = useState([]);
  const [selectedRole, setSelectedRole] = useState('view');
  const [notifyUsers, setNotifyUsers] = useState(true);
  const [notifyMessage, setNotifyMessage] = useState('');
  
  // Derive whether we have selections
  const hasSelection = selectedPrincipals.length > 0;
  
  // State for manage access section
  const [grants, setGrants] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [sharing, setSharing] = useState(false);
  const [error, setError] = useState(null);
  
  // Use focus trap hook for modal focus management
  // Keep trap active during closing animation to prevent focus jump
  const modalRef = useFocusTrap(mounted, {
    initialFocus: true,
    initialFocusSelector: '#share-dialog-title',
    initialFocusDelay: reduceMotion ? 0 : 180,
    restoreFocus: true,
  });

  // Lock body scroll while dialog is open
  useBodyScrollLock(true); // lock for the lifetime of this portal

  useEffect(() => {
    // trigger enter transition on mount (next frame)
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const startClose = useCallback(() => {
    // prevent double-trigger
    if (isClosing) return;
    setIsClosing(true);
    // If there will be no transitions (e.g., reduced motion), close immediately.
    if (reduceMotion) {
      // Next frame to allow data-state to update (avoids layout thrash)
      requestAnimationFrame(onClose);
    }
  }, [isClosing, onClose, reduceMotion]);

  // Check if user is admin and fetch grants
  useEffect(() => {
    if (!recordId) {
      setIsAdmin(false);
      setGrants([]);
      setLoading(false);
      return () => {};
    }

    let isActive = true;
    const requestId = initRequestRef.current + 1;
    initRequestRef.current = requestId;

    const sessionKey = `${recordId}:${ownerId || ''}:${viewerId || ''}`;
    const isNewSession = initializedSessionRef.current !== sessionKey;
    if (isNewSession) {
      initializedSessionRef.current = sessionKey;
      setLoading(true);
    }
    setError(null);

    let unsubscribe = () => {};

    const initialize = async () => {
      try {
        // Check admin status
        const adminStatus = await accessControlService.isUserAdmin({
          id: recordId,
          access: { owner: ownerId },
        }, { forceRefresh: true });
        if (!isActive || initRequestRef.current !== requestId) return;

        setIsAdmin(adminStatus);

        if (!adminStatus) {
          setGrants([]);
          return;
        }

        // Fetch current grants (admin only)
        const currentGrants = await accessControlService.fetchGrants(recordId);
        if (!isActive || initRequestRef.current !== requestId) return;

        setGrants(currentGrants);
        unsubscribe = accessControlService.subscribeToGrants(
          recordId,
          (updatedGrants) => {
            if (!isActive || initRequestRef.current !== requestId) return;
            setGrants(updatedGrants);
          }
        );
      } catch (err) {
        if (!isActive || initRequestRef.current !== requestId) return;

        console.error('Error initializing share dialog:', err);
        setIsAdmin(false);
        setGrants([]);
        setError(
          mapRecordsErrorToKey(err, {
            fallbackKey: 'records.share.errors.load_permissions_failed'
          })
        );
      } finally {
        if (!isActive || initRequestRef.current !== requestId) return;
        setLoading(false);
      }
    };
    
    initialize();

    return () => {
      isActive = false;
      unsubscribe();
    };
  }, [recordId, ownerId, viewerId]);


  // Handle notify toggle change
  const handleNotifyChange = (value) => {
    setNotifyUsers(value);
    // Clear message when turning off notifications
    if (!value) {
      setNotifyMessage('');
    }
  };

  // Handle sharing new access
  const handleShare = async () => {
    if (!isAdmin || selectedPrincipals.length === 0 || !recordId) return;

    const shareablePrincipals = selectedPrincipals.filter((principal) => {
      return !(principal.type === 'user' && principal.id === ownerId);
    });

    if (shareablePrincipals.length !== selectedPrincipals.length) {
      setSelectedPrincipals(shareablePrincipals);
    }

    if (shareablePrincipals.length === 0) {
      setError('records.share.errors.cannot_share_owner');
      return;
    }
    
    setSharing(true);
    setError(null);
    
    try {
      const notifyPayload = notifyUsers ?
        { enabled: true, message: notifyMessage } :
        { enabled: false };

      // Grant access to each selected principal
      const promises = shareablePrincipals.map((principal) =>
        accessControlService.grantAccess(recordId, principal, selectedRole, {
          notify: notifyPayload
        })
      );
      
      const results = await Promise.all(promises);

      const notifyFailures = results.filter(
        (result) => result?.notify?.requested && result.notify.error
      );
      if (notifyFailures.length > 0) {
        toast.warning(
          t(
            'records.share.toasts.notify_partial_failure',
            'Shared, but notifications could not be sent.'
          )
        );
      }
      
      // Clear selections
      setSelectedPrincipals([]);
      setSelectedRole('view');
      setNotifyMessage('');
      toast.success(
        t('records.share.toasts.shared_success', {
          count: shareablePrincipals.length,
          defaultValue:
            shareablePrincipals.length === 1
              ? 'Access shared successfully.'
              : `Access shared with ${shareablePrincipals.length} recipients.`
        })
      );
    } catch (err) {
      console.error('Error granting access:', err);
      setError(
        mapRecordsErrorToKey(err, {
          fallbackKey: 'records.share.errors.grant_failed'
        })
      );
    } finally {
      setSharing(false);
    }
  };

  // Handle role change for existing grant
  const handleRoleChange = async (grant, newRole) => {
    if (!isAdmin || !recordId) return;
    
    try {
      const principal = accessControlService.parsePrincipalKey(grant.id);
      await accessControlService.grantAccess(recordId, principal, newRole);
    } catch (err) {
      console.error('Error changing role:', err);
      setError(
        mapRecordsErrorToKey(err, {
          fallbackKey: 'records.share.errors.role_change_failed'
        })
      );
    }
  };

  // Handle removing access
  const handleRemoveAccess = async (grant) => {
    if (!isAdmin || !recordId) return;
    
    try {
      const principal = accessControlService.parsePrincipalKey(grant.id);
      await accessControlService.revokeAccess(recordId, principal);
    } catch (err) {
      console.error('Error removing access:', err);
      setError(
        mapRecordsErrorToKey(err, {
          fallbackKey: 'records.share.errors.remove_failed'
        })
      );
    }
  };

  const stopDialogPropagation = useCallback((event) => {
    event.stopPropagation();
  }, []);

  // overlay click → close (animate out)
  const handleOverlayClick = (e) => {
    e.stopPropagation();
    if (e.target === e.currentTarget) startClose();
  };

  // When the overlay's opacity transition ends in 'closing' state, unmount
  const handleOverlayTransitionEnd = (e) => {
    if (!isClosing) return;
    if (e.target !== overlayRef.current) return;
    // Only respond to opacity to avoid false positives
    if (e.propertyName !== 'opacity') return;
    onClose();
  };

  const pickerExcludeIds = Array.from(new Set([
    ...grants.map((grant) => grant.id),
    ...(ownerId ? [`user:${ownerId}`] : []),
  ]));

  return createPortal(
    <div 
      ref={overlayRef}
      className={styles.modalOverlay} 
      onClick={handleOverlayClick}
      onMouseDown={stopDialogPropagation}
      // data-state drives overlay fade on mobile
      data-state={isClosing ? "closing" : mounted ? "open" : "closed"}
      onTransitionEnd={handleOverlayTransitionEnd}
    >
      <div
        ref={stageRef}
        className={styles.sheetStage}
        data-state={isClosing ? "closing" : mounted ? "open" : "closed"}
      >
        <div 
          ref={modalRef}
          className={styles.modalContent}
          role="dialog"
          aria-modal="true"
          aria-labelledby="share-dialog-title"
          onKeyDown={(e) => { if (e.key === 'Escape') startClose(); }}
          onMouseDown={stopDialogPropagation}
          onClick={stopDialogPropagation}
        >
        {/* Header */}
        <div className={styles.modalHeader}>
          <h2 
            id="share-dialog-title" 
            className={styles.title}
            tabIndex={-1}
          >
            {t('records.share.title', {
              title: record.content?.title || t('records.views.note.card.untitled', 'Untitled'),
              defaultValue: `Share "${record.content?.title || 'Untitled'}"`
            })}
          </h2>
          <button
            onClick={startClose} // CHANGED
            className={styles.closeButton}
            aria-label={t('common.close', 'Close')}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className={styles.modalBody}>
          {loading ? (
            <div className={styles.loading}>
              {t('records.share.loading_permissions', 'Loading permissions...')}
            </div>
          ) : (
            <>
              {/* Quick Add Section */}
              {isAdmin && (
                <section className={styles.quickAddSection}>
                  <h3 className={styles.sectionTitle}>
                    {t('records.share.quick_add.title', 'Add people and groups')}
                  </h3>
                  
                  <div className={styles.quickAddForm}>
                    <PeopleGroupPicker
                      value={selectedPrincipals}
                      onChange={setSelectedPrincipals}
                      placeholder={t(
                        'records.share.quick_add.search_placeholder',
                        'Search for people and groups...'
                      )}
                      disabled={!isAdmin || sharing}
                      excludeIds={pickerExcludeIds}
                    />
                    
                    {/* Controls appear only after a selection exists */}
                    {hasSelection && (
                      <div className={styles.quickAddControlsGrid} data-state="open">
                        <RoleSelect
                          value={selectedRole}
                          onChange={setSelectedRole}
                          disabled={!isAdmin || sharing}
                          showDescription={false}
                          className={styles.roleCell}
                          iconMode="auto"
                        />
                        
                        <NotifyToggle
                          notifyUsers={notifyUsers}
                          onNotifyChange={handleNotifyChange}
                          disabled={!isAdmin || sharing}
                          className={styles.toggleCell}
                        />
                        
                        <NotifyTextarea
                          notifyUsers={notifyUsers}
                          message={notifyMessage}
                          onMessageChange={setNotifyMessage}
                          disabled={!isAdmin || sharing}
                          className={styles.messageRow}
                        />
                      </div>
                    )}
                  </div>
                  
                  {error && (
                    <div className={styles.error}>{t(error)}</div>
                  )}
                  
                  {/* Share action is also gated by selection */}
                  {hasSelection && (
                    <div className={styles.quickAddActions}>
                      <Button
                        variant="primary"
                        onClick={handleShare}
                        disabled={!isAdmin || sharing}
                      >
                        {sharing
                          ? t('records.share.sharing', 'Sharing...')
                          : t('common.share', 'Share')}
                      </Button>
                    </div>
                  )}
                </section>
              )}

              {/* Manage Access Section */}
              <section className={styles.manageAccessSection}>
                <h3 className={styles.sectionTitle}>
                  {t('records.share.manage.title', 'Who has access')}
                </h3>
                
                {!isAdmin && (
                  <div className={styles.nonAdminMessage}>
                    {t(
                      'records.share.manage.non_admin_message',
                      'You can view who has access. Only admins can change permissions.'
                    )}
                  </div>
                )}
                
                <ManageAccessTable
                  record={record}
                  grants={grants}
                  currentUserId={user?.uid}
                  isAdmin={isAdmin}
                  onRoleChange={handleRoleChange}
                  onRemove={handleRemoveAccess}
                />
              </section>
            </>
          )}
        </div>
      </div>
      </div>
    </div>,
    document.body
  );
};

export default ShareDialog;
